// The lab's printed documents (docs/plans/phase1-lab.md §8.3, §8.6): the 50 × 25 mm sample
// label and the A4/A5 report. Black on white only; identifiers in the mono face.
import { CULTURE_OUTCOME_WORDS, SUSCEPTIBILITY_WORDS } from '@wodi/domain';
import type { CultureView, LabReportView, LabSampleView } from '@wodi/shared';
import {
  Barcode,
  BarcodeArea,
  formatShortAge,
  PrintFooter,
  PrintHeader,
  PrintIdentifier,
  PrintLines,
  PrintSheet,
  PrintTable,
} from '@wodi/ui';
import type { Ref } from 'react';

import { formatInstant } from '../patients/format.ts';
import { flagOf } from './format.ts';
import styles from './lab.module.css';

/**
 * One label per sample (C15): patient name and patient number (two identifiers), the
 * sample number as a barcode and in print, specimen, test short names, collection time.
 */
export function SampleLabel({ sample }: { sample: LabSampleView }) {
  return (
    <PrintSheet paper="label-50x25" label={`Label for sample ${sample.sampleNumber}`}>
      <div className={styles.labelName}>{sample.patient.displayName}</div>
      <div className={styles.labelLine}>
        <PrintIdentifier>{sample.patient.patientNumber}</PrintIdentifier>
        <span>{formatInstant(sample.collectedAt)}</span>
      </div>
      <BarcodeArea value={sample.sampleNumber}>
        <Barcode value={sample.sampleNumber} heightMm={8} />
      </BarcodeArea>
      <div className={styles.labelLine}>
        <span>{sample.specimenType.display}</span>
        <span>{sample.orders.map((o) => o.shortNames).join(', ')}</span>
      </div>
    </PrintSheet>
  );
}

/**
 * Several labels printed as one job: each label is its own 50 × 25 mm page. `printSheet`
 * reads the paper size from this wrapper.
 */
export function LabelRun({
  samples,
  copies,
  runRef,
}: {
  samples: readonly LabSampleView[];
  copies: number;
  runRef: Ref<HTMLDivElement>;
}) {
  return (
    <div ref={runRef} data-paper="label-50x25" className={styles.labelRun}>
      {samples.flatMap((sample) =>
        Array.from({ length: copies }, (_, copy) => (
          <SampleLabel key={`${sample.id}-${String(copy)}`} sample={sample} />
        )),
      )}
    </div>
  );
}

export interface ReportPrintData {
  facilityName: string;
  paper: 'a4' | 'a5';
  patient: {
    displayName: string;
    patientNumber: string;
    sex: string;
    age: { unit: 'days' | 'months' | 'years'; value: number } | null;
  };
  sampleNumber: string;
  specimen: string;
  collectedAt: string;
  receivedAt: string | null;
  requester: string | null;
  report: LabReportView;
  /** Which copy this is, once printed before. */
  copy: number;
}

const PROVISIONAL_NOTE = 'Provisional range, not yet approved by the clinical lead.';

/** The lab report (plan §8.6), with a "Corrected report" header and reason where it applies. */
export function LabReportSheet({
  data,
  sheetRef,
}: {
  data: ReportPrintData;
  sheetRef?: Ref<HTMLElement>;
}) {
  const { report } = data;
  const corrected = report.version > 1;
  const anyProvisional = report.results.some((r) => r.range?.provisional === true);
  const title = `${corrected ? 'Corrected lab report' : 'Lab report'}${
    data.copy > 1 ? `, copy ${String(data.copy)}` : ''
  }`;
  return (
    <PrintSheet paper={data.paper} label={title} {...(sheetRef ? { ref: sheetRef } : {})}>
      <PrintHeader facility={data.facilityName} title={title} />
      <PrintLines
        items={[
          { label: 'Patient', value: data.patient.displayName },
          { label: 'Patient no.', value: data.patient.patientNumber, identifier: true },
          {
            label: 'Sex and age',
            value: `${data.patient.sex}, ${formatShortAge(data.patient.age, false)}`,
          },
          { label: 'Sample no.', value: data.sampleNumber, identifier: true },
          { label: 'Requested by', value: data.requester ?? 'Not recorded' },
          { label: 'Specimen', value: data.specimen },
          { label: 'Collected', value: formatInstant(data.collectedAt) },
          ...(data.receivedAt === null
            ? []
            : [{ label: 'Received', value: formatInstant(data.receivedAt) }]),
          ...(report.released === null
            ? []
            : [{ label: 'Released', value: formatInstant(report.released.at) }]),
        ]}
      />
      {corrected && report.correctionReason !== null ? (
        <p className={styles.printNote}>
          <strong>Corrected report.</strong> {report.correctionReason}
        </p>
      ) : null}
      <PrintTable>
        <thead>
          <tr>
            <th>{report.testName}</th>
            <th data-numeric="">Result</th>
            <th>Flag</th>
            <th>Unit</th>
            <th>Reference range</th>
          </tr>
        </thead>
        <tbody>
          {report.results.map((r) => {
            const flag = flagOf(r.interpretation);
            return (
              <tr key={r.id}>
                <td>{r.analyteName}</td>
                <td data-numeric="" className={flag.emphasised ? styles.strong : undefined}>
                  {r.display}
                </td>
                <td className={flag.emphasised ? styles.strong : undefined}>
                  {flag.marker === '' ? flag.word : `${flag.marker} ${flag.word}`}
                </td>
                <td>{r.unit?.display ?? ''}</td>
                <td>
                  {r.range === null
                    ? r.resultType === 'numeric'
                      ? 'No range set'
                      : ''
                    : `${r.range.text}${r.range.provisional ? ' *' : ''}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </PrintTable>
      {report.results.some((r) => r.comment !== null) ? (
        <PrintLines
          items={report.results.flatMap((r) =>
            r.comment === null ? [] : [{ label: r.analyteName, value: r.comment }],
          )}
        />
      ) : null}
      {report.culture === null ? null : <CulturePrint culture={report.culture} />}
      {report.comment === null ? null : <p className={styles.printNote}>{report.comment}</p>}
      {report.reportNote === null ? null : <p className={styles.printNote}>{report.reportNote}</p>}
      <PrintLines
        items={[
          ...(report.technicallyValidated === null
            ? []
            : [
                {
                  label: 'Validated by',
                  value: `${report.technicallyValidated.by.displayName}, ${formatInstant(report.technicallyValidated.at)}`,
                },
              ]),
          ...(report.released === null
            ? []
            : [
                {
                  label: report.technicallyValidated === null ? 'Validated by' : 'Authorised by',
                  value: `${report.released.by.displayName}, ${formatInstant(report.released.at)}`,
                },
              ]),
        ]}
      />
      <PrintFooter>
        {anyProvisional ? `* ${PROVISIONAL_NOTE} ` : ''}H high, L low, HH critical high, LL critical
        low, A abnormal, AA critical.
      </PrintFooter>
    </PrintSheet>
  );
}

/** The culture on a printed report: outcome, organisms and S, I, R as the lab entered them. */
function CulturePrint({ culture }: { culture: CultureView }) {
  return (
    <>
      <PrintLines
        items={[
          { label: 'Culture', value: CULTURE_OUTCOME_WORDS[culture.outcome] },
          ...(culture.comment === null
            ? []
            : [{ label: 'Culture comment', value: culture.comment }]),
        ]}
      />
      {culture.isolates.map((isolate) => (
        <PrintTable key={isolate.isolateNumber}>
          <thead>
            <tr>
              <th>
                {`Isolate ${String(isolate.isolateNumber)}: ${isolate.organismName}${isolate.quantity === null ? '' : `, ${isolate.quantity}`}`}
              </th>
              <th>Result</th>
              <th data-numeric="">MIC</th>
              <th data-numeric="">Zone</th>
            </tr>
          </thead>
          <tbody>
            {isolate.susceptibilities.map((s) => (
              <tr key={s.antibioticId}>
                <td>{s.antibioticName}</td>
                <td>{`${s.result} ${SUSCEPTIBILITY_WORDS[s.result]}`}</td>
                <td data-numeric="">{s.mic ?? ''}</td>
                <td data-numeric="">{s.zoneMm === null ? '' : `${String(s.zoneMm)} mm`}</td>
              </tr>
            ))}
          </tbody>
        </PrintTable>
      ))}
    </>
  );
}
